export type RegisterErrorCode = 'emailTaken' | 'invalidEmail' | 'unknown';

export interface NormalizedRegisterError {
  code: RegisterErrorCode;
  /** Raw server/error message, surfaced as the fallback for `unknown`. */
  message: string;
}

// Known backend (users-permissions) error strings we map to friendly,
// localized copy. Centralized here so both sign-up paths classify identically
// and there is a single place to update if the backend wording changes.
const EMAIL_TAKEN = 'Email or Username are already taken';
const INVALID_EMAIL = 'email must be a valid email';

const extractMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: { message?: string }; message?: string } } })
      .response;
    return response?.data?.error?.message || response?.data?.message || '';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return '';
};

/**
 * Classifies a failed registration request into a semantic code plus the raw
 * message. Callers map the code to their own i18n namespace and fall back to
 * `message` for `unknown`.
 */
export function normalizeRegisterError(error: unknown): NormalizedRegisterError {
  const message = extractMessage(error);

  if (message === EMAIL_TAKEN) {
    return { code: 'emailTaken', message };
  }
  if (message === INVALID_EMAIL) {
    return { code: 'invalidEmail', message };
  }
  return { code: 'unknown', message };
}
